import type {
    Characteristic,
    Component,
    PackageCharacteristic,
    PackageValueRef,
} from '../../schema';
import type { ConfigentoStore } from '../../state/store';

/**
 * Apply or revert a Package Characteristic's cascade. Each
 * {@link PackageValueRef} on the package points at a sub-Characteristic
 * by `(groupIndex, characteristicIndex)` and carries the value-ids the
 * package wants to preselect there. Mirrors the legacy
 * `Justselling_Configento_Option_Type_Package` semantics:
 *
 *   - active=true  → for every ref, set the sub-Char's selection to its
 *     `defaultValueId` (or `availableValues[0]` when no default given);
 *     multi-pick targets get every default applied, single-pick targets
 *     get only the first
 *   - active=false → clear those same sub-Char selections, leaving the
 *     rest of the configurator alone
 *
 * Idempotent on either direction — applying twice produces the same
 * end state as applying once. The cascade DOES NOT mark the targets as
 * `userTouched`, so a later rule-driven predefine can still overwrite
 * them if relevant.
 */
export function applyPackage(
    component: Component,
    pkg: PackageCharacteristic,
    store: ConfigentoStore,
    active: boolean,
): void {
    const state = store.getState();
    for (const ref of pkg.packageValues) {
        const target = lookupTarget(component, ref);
        if (!target) continue;
        if (active) {
            const valueIds = chooseValuesForActivation(ref);
            if (valueIds.length === 0) continue;
            applyToTarget(state, target, valueIds);
        } else {
            // Hard reset — the sub-Char goes back to "none", same as
            // the merchant clicking its reset affordance.
            state.resetSelection(target.id);
        }
    }
    // Record the package's own state. We use `single` selection with
    // valueId=1 as the "active" sentinel — Package has no meaningful
    // numeric id of its own to pick, and the existing single-pick
    // selection kind already round-trips through restoreFromAssetJson.
    if (active) {
        state.setSelection(pkg.id, { kind: 'single', valueId: 1 });
    } else {
        state.resetSelection(pkg.id);
    }
}

function lookupTarget(
    component: Component,
    ref: PackageValueRef,
): Characteristic | undefined {
    // The admin stores the target's id next to its position; the id survives
    // a reorder that the position would not.
    if (ref.id > 0) {
        for (const group of component.groups) {
            const byId = group.characteristics.find((c) => c.id === ref.id);
            if (byId) return byId;
        }
    }
    const group = component.groups[ref.groupIndex];
    return group?.characteristics[ref.characteristicIndex];
}

function chooseValuesForActivation(ref: PackageValueRef): number[] {
    // `defaultChoiceId` wins; admins explicitly chose those. Fall back
    // to the first `availableChoice` if no default was set, matching
    // the legacy `selectPackageOptions` behaviour where the first
    // available choice was activated.
    if (ref.defaultValueId.length > 0) return ref.defaultValueId;
    if (ref.availableValues.length > 0) return [ref.availableValues[0]!];
    return [];
}

function applyToTarget(
    state: ReturnType<ConfigentoStore['getState']>,
    target: Characteristic,
    valueIds: number[],
): void {
    switch (target.type) {
        case 'select':
        case 'radiobuttons':
            // Single-pick — take the first id.
            state.setSelection(target.id, { kind: 'single', valueId: valueIds[0] ?? null });
            return;
        case 'multiselect':
        case 'checkbox':
            state.setSelection(target.id, { kind: 'multi', valueIds });
            return;
        case 'listimage':
            state.setSelection(target.id, target.isMultiple
                ? { kind: 'multi', valueIds }
                : { kind: 'single', valueId: valueIds[0] ?? null },
            );
            return;
        case 'multiquantity': {
            const quantities: Record<number, number> = {};
            for (const id of valueIds) quantities[id] = 1;
            state.setSelection(target.id, { kind: 'quantities', quantities });
            return;
        }
        // Other types don't take a value-id cascade — skip silently.
        default:
            return;
    }
}
